import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  Users, 
  Megaphone,
  HeartHandshake,
  MessageCircleQuestion,
  Download,
  Play,
  CheckCircle2,
  ArrowRight,
  Database,
  FileText,
  Settings,
  Bot,
  Home,
  Mail,
  UserCheck,
  Lock,
  Filter,
  LayoutDashboard,
  Target,
  Briefcase,
  Award,
  TrendingUp,
  Globe,
  BookOpen,
  Sparkles,
  Laptop,
  Palette,
  Video,
  FileSpreadsheet,
  X,
  MapPin,
  Calendar,
  Wine,
  Flower2,
  Folder,
  FolderOpen,
  Music,
  DollarSign,
  Newspaper,
  Building2,
  Instagram,
  Linkedin,
  Loader2,
  LogOut
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from './SEO';
import { EditableText, EditableImage, EditableSection, useAdmin } from './AdminContentEditor';

// LOGOS - Updated with local assets
const logos = {
  womenBusinessSoiree: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png',
  oceanAI: '/assets/oceanailogowhite.png',
  dataDreaming: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/d7ltuzdn_datadreaminglogowhite.png',
  naomi: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
  notion: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png',
  canva: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/15zeamoo_CP7Z_quEhYkDEAE%3D.webp',
  airtable: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6if1ce6z_09d47a15534609898b3c26b98664751f.jpg',
  milanote: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6sre8ahc_PGX9pn9g_400x400.jpg',
  runway: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/wkx9prw2_eca69951-0148-4dd5-ae85-5a1de75251c6_Runway%2Bsquare.avif',
  perplexity: '/assets/perplexity.jpg',
  spotify: 'https://storage.googleapis.com/pr-newsroom-wp/1/2018/11/Spotify_Logo_RGB_Green.png',
  forbes: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Forbes_logo.svg/1280px-Forbes_logo.svg.png',
  oldvine: '/assets/oldvine.png',
  wines1010: '/assets/1010wines.png',
  jotform: 'https://customer-assets.emergentagent.com/job_085bfc62-3b65-48af-bb7c-d17ef39ed429/artifacts/zqtxdnzh_jotformlogo.png',
};

// Color palette - Dark background with pink/coral pops
const colors = {
  primary: '#E86A4A',       // Coral
  secondary: '#FDC4E0',     // Pink
  background: '#221e1e',    // Dark brown/charcoal
  backgroundAlt: '#1a1717', // Slightly darker
  card: '#FFFFFF',
  cardDark: 'rgba(255,255,255,0.08)',
  text: '#FFFFFF',
  textMuted: 'rgba(255,255,255,0.7)',
  accent: '#F472B6',        // Pink accent
  coral: '#E86A4A',
  pink: '#FDC4E0',
};

// Workshop sections
const sections = [
  { id: 'welcome', title: 'Welcome', icon: Calendar },
  { id: 'about', title: 'About', icon: Users },
  { id: 'agenda', title: 'Agenda', icon: BookOpen },
  { id: 'operations', title: 'Operations', icon: Settings },
  { id: 'marketing', title: 'Marketing', icon: Megaphone },
  { id: 'customer-service', title: 'Customer Service', icon: HeartHandshake },
  { id: 'qa', title: 'Q&A', icon: MessageCircleQuestion },
];

// Journey folders
const journeyFolders = [
  { icon: Laptop, label: 'Age 6', desc: 'Built first computer', color: '#60A5FA' },
  { icon: Users, label: '30+ Creatives', desc: 'Digital agency', color: '#F472B6' },
  { icon: Building2, label: 'Startup Founder', desc: "acquired in 23'", color: '#F87171' },
];

// Lightbox Component
const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        <motion.img
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          src={src}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Access Gate - OAuth based
const AccessGate = ({ onAccessGranted, user, isAuthenticated, isLoading, login, logout }) => {
  const [status, setStatus] = useState('idle'); // idle, checking, not_approved
  const API_URL = window.location.origin;

  useEffect(() => {
    const checkAccess = async () => {
      if (isAuthenticated && user) {
        setStatus('checking');
        try {
          // Check if user has access
          const response = await fetch(`${API_URL}/api/admin/check/${encodeURIComponent(user.email)}`);
          const data = await response.json();
          
          if (data.is_admin) {
            localStorage.setItem('workshop_access', 'granted');
            localStorage.setItem('soiree_approved', 'true');
            localStorage.setItem('soiree_email', user.email);
            onAccessGranted();
          } else {
            // Check page-specific access
            const accessResponse = await fetch(`${API_URL}/api/admin/check-page-access/women-biz-soiree/${encodeURIComponent(user.email)}`);
            const accessData = await accessResponse.json();
            
            if (accessData.has_access) {
              localStorage.setItem('workshop_access', 'granted');
              localStorage.setItem('soiree_approved', 'true');
              localStorage.setItem('soiree_email', user.email);
              onAccessGranted();
            } else {
              setStatus('not_approved');
            }
          }
        } catch (error) {
          console.error('Access check failed:', error);
          setStatus('not_approved');
        }
      }
    };
    
    checkAccess();
  }, [isAuthenticated, user, onAccessGranted, API_URL]);

  const handleGoogleSignIn = () => {
    sessionStorage.setItem('auth_return_url', window.location.href);
    sessionStorage.setItem('pending_lab_access', '/womenbusinesssoiree');
    login(window.location.href);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: colors.primary }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: colors.primary }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/80">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: colors.primary }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        
        {/* WBS Logo */}
        <div className="mb-8">
          <img src={logos.womenBusinessSoiree} alt="Women Business Soirée" className="h-40 mx-auto object-contain" />
        </div>

        <div className="rounded-3xl p-8 bg-white/95 shadow-2xl">
          {status === 'not_approved' ? (
            <>
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-red-100">
                <Lock className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Not Approved</h2>
              <p className="text-gray-500 mb-2">Your account is not approved for this workshop.</p>
              <p className="text-gray-400 text-sm mb-6">Signed in as: {user?.email}</p>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 mb-3"
              >
                <LogOut className="w-4 h-4" />
                Sign Out & Try Different Account
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: `${colors.primary}20` }}>
                <Lock className="w-8 h-8" style={{ color: colors.primary }} />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Workshop Access</h2>
              <p className="text-gray-500 mb-6">Sign in with Google to access the workshop content.</p>
              
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>

              <p className="text-gray-400 text-xs mt-6">Workshop access is for event attendees only</p>
            </>
          )}
        </div>

        <Link to="/" className="inline-flex items-center gap-2 text-white/80 text-sm mt-8 hover:text-white">
          <Home className="w-4 h-4" />
          Back to Data Dreaming
        </Link>
      </motion.div>
    </div>
  );
};

// Email Modal
const EmailModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => { onClose(); setSubmitted(false); setEmail(''); }, 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="w-full max-w-md rounded-3xl p-8 bg-white" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: colors.secondary }}>
                  <Mail className="w-8 h-8" style={{ color: colors.primary }} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Your Workshop Recap</h3>
                <p className="text-gray-600 text-sm">All tools and resources from today</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required className="w-full px-4 py-3 rounded-xl border border-gray-300" />
                <button type="submit" className="w-full py-3 rounded-xl font-medium text-white" style={{ background: colors.primary }}>
                  Email Me Today's Workshop Recap
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4" style={{ color: colors.primary }} />
              <h3 className="text-xl font-semibold text-gray-900">Check your inbox!</h3>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Tool Badge
const ToolBadge = ({ name, logo, primary }) => (
  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${primary ? 'bg-white text-gray-900' : 'bg-white/20 text-white'}`}>
    <img src={logo} alt={name} className="w-5 h-5 object-contain rounded" />
    <span className="text-sm font-medium">{name}</span>
  </div>
);

// Section Header with pink/coral accents - bolder text
const SectionHeader = ({ title, subtitle, duration }) => (
  <div className="text-center mb-12">
    {duration && (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-4 font-semibold" style={{ background: colors.pink, color: colors.coral }}>
        <Clock className="w-4 h-4" />
        <span>{duration}</span>
      </div>
    )}
    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{title}</h2>
    {subtitle && <p className="text-lg font-medium text-white/80 max-w-2xl mx-auto">{subtitle}</p>}
  </div>
);

// Journey Folder Card
const JourneyFolder = ({ icon: Icon, label, desc, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="relative"
  >
    <div className="p-4 rounded-xl bg-white/10 hover:bg-white/15 transition-all cursor-pointer border border-white/5">
      <div className="w-12 h-12 rounded-lg mb-3 flex items-center justify-center" style={{ background: `${color}30` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <p className="text-white font-semibold text-sm">{label}</p>
      <p className="text-white/50 text-xs">{desc}</p>
    </div>
  </motion.div>
);

// Main Component
export const WorkshopPage = () => {
  const { user, loading: authLoading, isAuthenticated, login, logout } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '', isOpen: false });
  const sectionRefs = useRef([]);

  const openLightbox = (src, alt) => {
    setLightboxImage({ src, alt, isOpen: true });
  };

  const closeLightbox = () => {
    setLightboxImage({ ...lightboxImage, isOpen: false });
  };

  // Admin emails for bypass (hardcoded fallback for production)
  const ADMIN_EMAILS = ['me@iamnaomithomas.com', 'naomi@datadreaming.cloud'];

  // Only allow access if user is authenticated and approved, or if admin
  useEffect(() => {
    const soireeEmail = localStorage.getItem('soiree_email');
    const soireeApproved = localStorage.getItem('soiree_approved');
    
    // Check if user is admin (bypass access gate)
    const isAdminEmail = soireeEmail && ADMIN_EMAILS.includes(soireeEmail.toLowerCase());
    
    if (isAdminEmail || soireeApproved === 'true') {
      // Admin or approved user - grant access immediately
      setHasAccess(true);
      return;
    }
    
    // If user is authenticated and email matches stored email, allow access
    if (isAuthenticated && user && soireeEmail === user.email) {
      const workshopAccess = localStorage.getItem('workshop_access');
      if (workshopAccess === 'granted') {
        setHasAccess(true);
      }
    } else if (!isAuthenticated && !isAdminEmail) {
      // Clear access if not authenticated and not admin
      setHasAccess(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const { offsetTop, offsetHeight } = ref;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(index);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index) => sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth' });
  const progress = ((activeSection + 1) / sections.length) * 100;

  if (!hasAccess) {
    return (
      <AccessGate 
        onAccessGranted={() => setHasAccess(true)} 
        user={user}
        isAuthenticated={isAuthenticated}
        isLoading={authLoading}
        login={login}
        logout={logout}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ background: colors.background }}>
      <SEO 
        title="AI for Business Growth Workshop | Women Business Soirée"
        description="Exclusive workshop content on leveraging AI tools for business operations, marketing, and growth."
        image={logos.womenBusinessSoiree}
        url="/womenbusinesssoiree"
      />
      <EmailModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-white/10" style={{ background: 'rgba(34,30,30,0.95)' }}>
        <div className="h-1 bg-white/10">
          <motion.div className="h-full" style={{ background: colors.coral }} animate={{ width: `${progress}%` }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 rounded-lg bg-white/10 hover:bg-white/20" aria-label="Home">
              <Home className="w-4 h-4 text-white" />
            </Link>
            <img src={logos.womenBusinessSoiree} alt="WBS" className="h-8 object-contain" />
          </div>
          
          <div className="hidden md:flex items-center gap-1">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <button key={section.id} onClick={() => scrollToSection(index)}
                  className={`p-2 rounded-lg transition-all ${activeSection === index ? 'text-white bg-white/20' : 'text-white/50 hover:text-white'}`}
                  title={section.title}>
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => scrollToSection(Math.max(0, activeSection - 1))} disabled={activeSection === 0}
              className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white/60 text-sm min-w-[60px] text-center">{activeSection + 1}/{sections.length}</span>
            <button onClick={() => scrollToSection(Math.min(sections.length - 1, activeSection + 1))} disabled={activeSection === sections.length - 1}
              className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="pt-16">

        {/* ========== WELCOME ========== */}
        <section ref={el => sectionRefs.current[0] = el} className="min-h-screen flex items-center justify-center px-4 pt-0 pb-4">
          <motion.div className="text-center max-w-3xl" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            {/* WBS Logo */}
            <img src={logos.womenBusinessSoiree} alt="Women Business Soirée" className="h-40 md:h-48 mx-auto object-contain mb-2" />

            {/* Simple tagline - all caps, smaller */}
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-8">
              MAKE YOUR DREAMS COME TRUE
            </p>

            {/* Event Info with pink accents - all on one line on mobile */}
            <div className="flex flex-nowrap justify-center gap-2 sm:gap-3 mb-8">
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-white" style={{ background: `${colors.pink}20`, border: `1px solid ${colors.pink}40` }}>
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.pink }} />
                <span className="text-xs sm:text-sm whitespace-nowrap">Los Angeles</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-white" style={{ background: `${colors.coral}20`, border: `1px solid ${colors.coral}40` }}>
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.coral }} />
                <span className="text-xs sm:text-sm whitespace-nowrap">Workshop</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-white" style={{ background: `${colors.pink}20`, border: `1px solid ${colors.pink}40` }}>
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.pink }} />
                <span className="text-xs sm:text-sm whitespace-nowrap">40 min</span>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">AI for Business Growth</h1>
            <p className="text-lg text-white/70 mb-4">Marketing, Operations & Customer Service</p>
            
            <button onClick={() => scrollToSection(1)} className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-white shadow-lg hover:opacity-90" style={{ background: colors.coral }}>
              Begin
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </section>

        {/* ========== ABOUT / MEET YOUR GUIDE ========== */}
        <section ref={el => sectionRefs.current[1] = el} className="px-4 py-20" style={{ background: colors.backgroundAlt }}>
          <motion.div className="max-w-4xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="Meet Your Guide" />
            
            {/* Curated By - text only, all caps, bolder - with mobile line breaks */}
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-white/60 font-semibold">
                <span className="block sm:inline">CURATED BY OCEAN AI</span>
                <span className="block sm:inline"> IN COLLABORATION</span>
                <span className="block sm:inline"> WITH DATA DREAMING</span>
              </p>
            </div>
            
            {/* Profile Card with My Journey under profile */}
            <div className="rounded-3xl p-6 sm:p-10" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${colors.pink}30` }}>
              {/* Profile Info - centered on top */}
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 mb-8">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  <img 
                    src={logos.naomi} 
                    alt="Naomi Thomas" 
                    className="w-32 h-32 md:w-36 md:h-36 rounded-2xl object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                    style={{ border: `3px solid ${colors.coral}` }}
                    onClick={() => openLightbox(logos.naomi, 'Naomi Thomas')}
                  />
                </div>

                {/* Profile Text */}
                <div className="text-center md:text-left flex-1">
                  <h3 className="text-2xl font-bold text-white mb-1">Naomi Thomas</h3>
                  <p className="mb-3 text-sm uppercase tracking-wide font-semibold" style={{ color: colors.pink }}>AI Strategist + Data Dreamer</p>
                  
                  {/* Social Links */}
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                    <a href="https://instagram.com/techbaenae" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                      <Instagram className="w-4 h-4 text-white/70" />
                      <span className="text-xs text-white/70">techbaenae</span>
                    </a>
                    <a href="https://linkedin.com/in/techbaenae" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                      <Linkedin className="w-4 h-4 text-white/70" />
                      <span className="text-xs text-white/70">techbaenae</span>
                    </a>
                  </div>
                  
                  {/* Mission */}
                  <p className="text-white/90 leading-relaxed font-medium text-sm md:text-base">
                    Helping builders, innovators, and data dreamers harness the power of AI and data to grow their businesses while keeping their authentic voice.
                  </p>
                </div>
              </div>

              {/* My Journey - row of 3 columns under profile */}
              <div>
                <p className="text-sm text-center mb-4 font-semibold" style={{ color: colors.pink }}>My Journey</p>
                <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
                  {journeyFolders.map((folder, i) => (
                    <JourneyFolder key={folder.label} {...folder} delay={i * 0.1} />
                  ))}
                </div>
              </div>
            </div>

            {/* AI Use Cases Section */}
            <div className="mt-12 rounded-3xl p-8" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${colors.coral}30` }}>
              <h3 className="text-xl font-bold text-white mb-6 text-center">My Perspective on AI</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Use Case 1: Leveling the playing field */}
                <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${colors.pink}30` }}>
                      <TrendingUp className="w-5 h-5" style={{ color: colors.pink }} />
                    </div>
                    <h4 className="font-semibold text-white">It Levels the Playing Field</h4>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    AI removes barriers. Startups that don't have access to a lot of capital can create fully functioning products and marketing campaigns for a 10th of the cost. It democratizes opportunity.
                  </p>
                </div>

                {/* Use Case 2: Part of creative process */}
                <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${colors.coral}30` }}>
                      <Sparkles className="w-5 h-5" style={{ color: colors.coral }} />
                    </div>
                    <h4 className="font-semibold text-white">It's Part of My Creative Process</h4>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    AI doesn't replace me. It helps with productivity and efficiency. I use whiteboards and notepads, take pictures of my notes, and have AI convert them into organized digital content.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ========== AGENDA ========== */}
        <section ref={el => sectionRefs.current[2] = el} className="min-h-screen px-4 py-20">
          <motion.div className="max-w-4xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="What You'll Learn" subtitle="Today's workshop agenda" />
            
            <div className="space-y-4">
              {[
                { icon: Settings, title: 'Operations', time: '10 min', desc: 'Build your digital HQ with Notion', tools: [logos.notion, logos.canva, logos.milanote] },
                { icon: Megaphone, title: 'Marketing', time: '10 min', desc: 'Content strategy with Canva & AI', tools: [logos.canva, logos.runway, logos.perplexity, logos.notion] },
                { icon: HeartHandshake, title: 'Customer Service', time: '10 min', desc: 'Feedback forms with Airtable', tools: [logos.airtable, logos.notion] },
                { icon: MessageCircleQuestion, title: 'Q&A', time: '10 min', desc: 'Discussion and resources', tools: [] },
              ].map((item, i) => (
                <motion.div key={item.title} onClick={() => scrollToSection(i + 3)}
                  className="p-4 sm:p-6 rounded-2xl bg-white/5 hover:bg-white/10 transition-all cursor-pointer border border-white/10"
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: colors.pink }}>
                      <item.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: colors.coral }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-base sm:text-lg font-semibold text-white">{item.title}</h3>
                        <span className="text-xs sm:text-sm flex-shrink-0" style={{ color: colors.pink }}>{item.time}</span>
                      </div>
                      <p className="text-white/50 text-xs sm:text-sm mb-2">{item.desc}</p>
                      {item.tools.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tools.map((tool, j) => (
                            <img key={j} src={tool} alt="tool" className="w-5 h-5 sm:w-6 sm:h-6 rounded object-contain" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Body Fruit - White background, colorful labels */}
            <div className="mt-12 p-8 rounded-3xl bg-white">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Today's Example</h3>
                <p className="text-gray-600 text-sm font-medium">Apply what you learn to your own company. No laptop? Follow along with Body Fruit!</p>
              </div>
              
              <div className="flex flex-row items-center justify-center gap-8 sm:gap-12 px-4 sm:px-8">
                {/* Logo */}
                <div className="flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/1ih2f3uc_body%20fruit%20logo.png', 'Body Fruit Logo')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/1ih2f3uc_body%20fruit%20logo.png" 
                    alt="Body Fruit Logo" 
                    className="w-32 sm:w-48 md:w-56 h-auto object-contain rounded-2xl"
                  />
                </div>
                
                {/* Product Mockup */}
                <div className="flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/udexn2h7_body%20fruit%20mockup.png', 'Body Fruit Products')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/udexn2h7_body%20fruit%20mockup.png" 
                    alt="Body Fruit Products" 
                    className="w-40 sm:w-64 md:w-80 h-auto object-contain rounded-2xl"
                  />
                </div>
              </div>
              
              {/* Products with colorful emoji labels - 2x2 grid on mobile */}
              <div className="mt-6">
                <p className="text-gray-700 text-sm text-center mb-1 font-semibold">Fruit-themed beauty products</p>
                <p className="text-gray-500 text-xs text-center mb-4 pl-4">for the modern woman:</p>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:justify-center">
                  <span className="px-3 py-2 rounded-full text-white text-xs sm:text-sm font-semibold shadow-md text-center" style={{ background: '#DC2626' }}>🍓 Strawberry Lotion</span>
                  <span className="px-3 py-2 rounded-full text-white text-xs sm:text-sm font-semibold shadow-md text-center" style={{ background: '#4F46E5' }}>🫐 Blueberry Lip Gloss</span>
                  <span className="px-3 py-2 rounded-full text-white text-xs sm:text-sm font-semibold shadow-md text-center" style={{ background: '#BE185D' }}>🍇 Raspberry Wash</span>
                  <span className="px-3 py-2 rounded-full text-white text-xs sm:text-sm font-semibold shadow-md text-center" style={{ background: '#EA580C' }}>🍑 Peach Hand Cream</span>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ========== OPERATIONS ========== */}
        <section ref={el => sectionRefs.current[3] = el} className="min-h-screen px-4 py-20" style={{ background: colors.backgroundAlt }}>
          <motion.div className="max-w-5xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="Operations" subtitle="Build your digital HQ and set the foundation" duration="10 minutes" />
            
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <ToolBadge name="Notion" logo={logos.notion} primary />
              <a href="https://canva.com" target="_blank" rel="noopener noreferrer"><ToolBadge name="Canva" logo={logos.canva} /></a>
              <a href="https://milanote.com" target="_blank" rel="noopener noreferrer"><ToolBadge name="Milanote" logo={logos.milanote} /></a>
            </div>

            {/* Notion Overview */}
            <div className="rounded-3xl p-6 sm:p-8 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-4 mb-4">
                <img src={logos.notion} alt="Notion" className="w-10 h-10 rounded-lg object-contain" />
                <h4 className="text-lg sm:text-xl font-bold text-white">What is Notion?</h4>
              </div>
              <p className="text-white/70 text-sm mb-4">
                Notion is an all-in-one workspace that combines notes, docs, databases, and project management into a single tool. Perfect for building your business operating system.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Notes & Docs', 'Databases', 'Task Management', 'Team Wiki'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: colors.coral }} />
                    <span className="text-xs text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity */}
            <div className="p-6 sm:p-8 rounded-3xl mb-8" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${colors.coral}30` }}>
              <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Play className="w-5 h-5" style={{ color: colors.coral }} />
                Activity: Build Your Digital HQ
              </h4>
              <p className="text-white/60 text-sm mb-6">Apply this to your own business. No laptop? Follow along with Body Fruit.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-xl bg-white/10">
                  <img src={logos.notion} alt="Notion" className="w-6 h-6 mx-auto mb-2 opacity-70" />
                  <p className="text-sm font-medium text-white">1. Create Workspace</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-white/10">
                  <img src={logos.notion} alt="Notion" className="w-6 h-6 mx-auto mb-2 opacity-70" />
                  <p className="text-sm font-medium text-white">2. Add Pages</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-white/10">
                  <img src={logos.notion} alt="Notion" className="w-6 h-6 mx-auto mb-2 opacity-70" />
                  <p className="text-sm font-medium text-white">3. Write First SOP</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-white/10">
                  <img src={logos.canva} alt="Canva" className="w-6 h-6 mx-auto mb-2 opacity-70" />
                  <p className="text-sm font-medium text-white">4. Build Flowchart</p>
                </div>
              </div>
            </div>

            {/* Notion HQ + SOPs side by side */}
            <div className="grid md:grid-cols-5 gap-6 mb-8">
              {/* Left: Notion HQ Screenshot - 3/5 width */}
              <div className="md:col-span-3 rounded-3xl p-4 sm:p-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h4 className="text-lg font-bold text-white mb-4">Body Fruit Digital HQ in Notion</h4>
                <div className="rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/ottnmsyb_Screenshot%202026-01-13%20at%207.45.59%E2%80%AFAM.png', 'Body Fruit Notion HQ')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/ottnmsyb_Screenshot%202026-01-13%20at%207.45.59%E2%80%AFAM.png" 
                    alt="Body Fruit Notion HQ Example" 
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Right: SOPs with Screenshot - 2/5 width */}
              <div className="md:col-span-2 rounded-2xl p-4 sm:p-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h4 className="font-semibold text-white mb-4">Setting Up SOPs</h4>
                <div className="rounded-xl overflow-hidden mb-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/ldzsx038_Screenshot%202026-01-13%20at%208.41.58%E2%80%AFAM.png', 'SOP Example')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/ldzsx038_Screenshot%202026-01-13%20at%208.41.58%E2%80%AFAM.png" 
                    alt="SOP Example in Notion" 
                    className="w-full h-auto object-contain"
                  />
                </div>
                <div className="p-3 rounded-lg text-sm" style={{ background: `${colors.pink}20`, color: colors.pink }}>
                  Use Notion AI to help draft SOPs faster
                </div>
              </div>
            </div>

            {/* User Flow - Full width */}
            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h4 className="font-semibold text-white mb-4">User Flow in Canva</h4>
              <div className="rounded-xl overflow-hidden mb-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/5apzp0sg_Screenshot%202026-01-13%20at%208.10.15%E2%80%AFAM.png', 'Customer Feedback User Flow')}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/5apzp0sg_Screenshot%202026-01-13%20at%208.10.15%E2%80%AFAM.png" 
                  alt="Customer Feedback User Flow" 
                  className="w-full h-auto object-contain"
                />
              </div>
              <p className="text-white/50 text-sm">Customer journey visualization</p>
            </div>
          </motion.div>
        </section>

        {/* ========== MARKETING ========== */}
        <section ref={el => sectionRefs.current[4] = el} className="min-h-screen px-4 py-20">
          <motion.div className="max-w-5xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="Marketing" subtitle="Use AI to enhance your creative process, not replace it" duration="10 minutes" />
            
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-3 mb-10">
              <ToolBadge name="Canva" logo={logos.canva} primary />
              <a href="https://runwayml.com" target="_blank" rel="noopener noreferrer"><ToolBadge name="Runway" logo={logos.runway} /></a>
              <a href="https://perplexity.ai" target="_blank" rel="noopener noreferrer"><ToolBadge name="Perplexity" logo={logos.perplexity} /></a>
              <a href="https://notion.so" target="_blank" rel="noopener noreferrer"><ToolBadge name="Notion" logo={logos.notion} /></a>
            </div>

            {/* Canva Overview */}
            <div className="rounded-3xl p-6 sm:p-8 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-4 mb-4">
                <img src={logos.canva} alt="Canva" className="w-10 h-10 rounded-lg object-contain" />
                <h4 className="text-lg sm:text-xl font-bold text-white">What is Canva?</h4>
              </div>
              <p className="text-white/70 text-sm mb-4">
                Canva is a free online design platform that makes it easy to create professional graphics, videos, and marketing materials. With AI-powered tools like Magic Media, you can generate videos from text prompts.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Graphic Design', 'Video Editing', 'AI Generation', 'Brand Kits'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: colors.coral }} />
                    <span className="text-xs text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Persona */}
            <div className="rounded-3xl p-4 sm:p-8 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h4 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">Target Audience Persona</h4>
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center flex-shrink-0 mx-auto md:mx-0" style={{ background: colors.pink }}>
                  <span className="text-4xl sm:text-5xl">🍓</span>
                </div>
                <div>
                  <h5 className="text-base sm:text-lg font-semibold text-white mb-2 text-center md:text-left">Meet Ashley</h5>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" style={{ color: colors.coral }} />
                      <span>Loves strawberries and wellness practices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" style={{ color: colors.coral }} />
                      <span>Wants to romanticize her daily routines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" style={{ color: colors.coral }} />
                      <span>Adult who wants luxury/modern style</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Problem/Solution */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="rounded-2xl p-4 sm:p-6" style={{ background: `${colors.coral}20`, border: `1px solid ${colors.coral}40` }}>
                <h5 className="font-semibold text-white mb-2">Problem</h5>
                <p className="text-white/70 text-sm">Finding bath products that align with her style without feeling like kid products</p>
              </div>
              <div className="rounded-2xl p-4 sm:p-6" style={{ background: `${colors.pink}20`, border: `1px solid ${colors.pink}40` }}>
                <h5 className="font-semibold text-white mb-2">Solution</h5>
                <p className="text-white/70 text-sm">Luxury fruit-themed bath and beauty products for sophisticated adults</p>
              </div>
            </div>

            {/* Brand Identity */}
            <div className="rounded-2xl p-6 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h5 className="font-semibold text-white mb-4">Body Fruit Brand Identity</h5>
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-sm text-white/50">Colors:</span>
                <div className="w-8 h-8 rounded-lg" style={{ background: '#DC2626' }} />
                <div className="w-8 h-8 rounded-lg" style={{ background: '#D4AF37' }} />
                <div className="w-8 h-8 rounded-lg" style={{ background: '#FDF2F8' }} />
                <span className="px-3 py-1 rounded-full text-sm" style={{ background: colors.pink, color: colors.coral }}>Luxury • Wellness • Modern</span>
              </div>
            </div>

            {/* Activity - Step-by-step video creation */}
            <div className="p-4 sm:p-8 rounded-3xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${colors.coral}30` }}>
              <h4 className="text-base sm:text-xl font-bold text-white mb-2 flex items-center gap-2 whitespace-nowrap">
                <Video className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: colors.coral }} />
                <span>Activity: Create a Video Ad in Canva</span>
              </h4>
              <p className="text-white/60 text-xs sm:text-sm mb-6">Apply this to your own brand. No laptop? Follow along with Body Fruit.</p>
              
              {/* Steps 1 & 2 side by side */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Step 1: Open Canva & Start New Video */}
                <div>
                  <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: colors.coral }}>1</span>
                    Open Canva & Start New Video
                  </h5>
                  <div className="rounded-xl overflow-hidden border border-white/10 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/4esacnwh_Screenshot%202026-01-13%20at%208.23.46%E2%80%AFAM.png', 'Open Canva')}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/4esacnwh_Screenshot%202026-01-13%20at%208.23.46%E2%80%AFAM.png" 
                      alt="Open Canva and search for video" 
                      className="w-full h-auto object-cover"
                    />
                    <div className="p-2 bg-white/5">
                      <p className="text-xs text-white/70 text-center">Search "video" to create a blank project</p>
                    </div>
                  </div>
                </div>

                {/* Step 2: Generate Video with Magic Media */}
                <div>
                  <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: colors.coral }}>2</span>
                    Generate Video with Magic Media
                  </h5>
                  <div className="rounded-xl overflow-hidden border border-white/10 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/j8osajb1_video%20ad%20prompt%20canva.png', 'Magic Media in Canva')}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/j8osajb1_video%20ad%20prompt%20canva.png" 
                      alt="Find Magic Media in Canva" 
                      className="w-full h-auto object-cover"
                    />
                    <div className="p-2 bg-white/5">
                      <p className="text-xs text-white/70 text-center">Click Magic Media and write your prompt</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: AI Generated Video */}
              <div className="mb-8">
                <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: colors.coral }}>3</span>
                  AI Generated Video (Landscape)
                </h5>
                <div className="rounded-xl overflow-hidden border border-white/10 max-w-3xl mx-auto">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-auto"
                  >
                    <source src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/vcsfkn1u_Body%20Fruit.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="p-2 bg-white/5">
                    <p className="text-xs text-white/70 text-center">AI generated landscape video for Body Fruit</p>
                  </div>
                </div>
              </div>

              {/* Step 4: Add Media & Text */}
              <div>
                <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: colors.coral }}>4</span>
                  Add Media & Text
                </h5>
                <div className="grid md:grid-cols-3 gap-4 items-stretch">
                  {/* Resize Screenshot - Left */}
                  <div className="rounded-xl overflow-hidden border border-white/10 flex flex-col cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/0yis7y2c_resize%20video%20canva.png', 'Resize Video in Canva')}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/0yis7y2c_resize%20video%20canva.png" 
                      alt="Resize video in Canva" 
                      className="w-full h-auto object-cover flex-1"
                    />
                    <div className="p-2 bg-white/5">
                      <p className="text-xs text-white/70 text-center">Resize for different platforms</p>
                    </div>
                  </div>

                  {/* Tips - Center */}
                  <div className="flex flex-col justify-center gap-2">
                    {[
                      'Add your brand colors & logo',
                      'Include a compelling tagline',
                      'Add background music',
                      'Export in multiple sizes'
                    ].map((tip, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: colors.coral }} />
                        <span className="text-xs text-white/80">{tip}</span>
                      </div>
                    ))}
                  </div>

                  {/* Final Video Reel - Right */}
                  <div className="rounded-xl border border-white/10 overflow-hidden flex flex-col">
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="w-full h-auto flex-1 object-cover"
                    >
                      <source src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/eo1h2e24_Body%20Fruit%20%28Mobile%20Video%29.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div className="p-2 bg-white/5">
                      <p className="text-xs text-white/70 text-center">Final video with branding</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ========== CUSTOMER SERVICE ========== */}
        <section ref={el => sectionRefs.current[5] = el} className="min-h-screen px-4 py-20" style={{ background: colors.backgroundAlt }}>
          <motion.div className="max-w-5xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="Customer Service" subtitle="Set up systems for customer connection" duration="10 minutes" />
            
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <ToolBadge name="Airtable" logo={logos.airtable} primary />
              <a href="https://jotform.com" target="_blank" rel="noopener noreferrer"><ToolBadge name="Jotform" logo={logos.jotform} /></a>
              <a href="https://notion.so" target="_blank" rel="noopener noreferrer"><ToolBadge name="Notion" logo={logos.notion} /></a>
            </div>

            {/* Airtable Overview */}
            <div className="rounded-3xl p-6 sm:p-8 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-4 mb-4">
                <img src={logos.airtable} alt="Airtable" className="w-10 h-10 rounded-lg object-contain" />
                <h4 className="text-lg sm:text-xl font-bold text-white">What is Airtable?</h4>
              </div>
              <p className="text-white/70 text-sm mb-4">
                Airtable is a flexible database platform that combines the simplicity of a spreadsheet with the power of a database. Connect forms, create filtered views, and build dashboards to track customer feedback.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Form Integration', 'Filtered Views', 'Automations', 'Dashboard Analytics'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: colors.coral }} />
                    <span className="text-xs text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Types */}
            <div className="rounded-3xl p-8 mb-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h4 className="text-xl font-bold text-white mb-6">Feedback Forms</h4>
              <div className="flex justify-center gap-2 sm:gap-4 mb-6">
                {[
                  { icon: UserCheck, label: 'Customers' },
                  { icon: Briefcase, label: 'Partners' },
                  { icon: Users, label: 'Team' },
                ].map((t) => (
                  <div key={t.label} className="text-center p-3 sm:p-6 rounded-2xl bg-white/10 flex-1 max-w-[120px] sm:max-w-none">
                    <t.icon className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3" style={{ color: colors.coral }} />
                    <p className="font-semibold text-white text-xs sm:text-base">{t.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center items-center gap-2 p-6 rounded-xl bg-white/5 mb-6">
                {['Multiple Forms', '→', 'One Base', '→', 'Filtered Views', '→', 'Dashboard Analytics'].map((s, i) => (
                  <span key={i} className={s === '→' ? 'text-white/30 hidden sm:inline' : 'px-3 py-2 rounded-lg bg-white/10 text-white text-xs sm:text-sm font-medium'}>{s}</span>
                ))}
              </div>

              {/* Form and Analytics Screenshots side by side */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Form Screenshot */}
                <div className="rounded-xl overflow-hidden border border-white/10 cursor-pointer hover:opacity-90 transition-opacity h-64 md:h-80" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/tnbznsy8_Screenshot%202026-01-13%20at%208.20.17%E2%80%AFAM.png', 'Customer Feedback Form')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/tnbznsy8_Screenshot%202026-01-13%20at%208.20.17%E2%80%AFAM.png" 
                    alt="Feedback Form Example" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Analytics Screenshot */}
                <div className="rounded-xl overflow-hidden border border-white/10 cursor-pointer hover:opacity-90 transition-opacity h-64 md:h-80" onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/yj5kr475_Screenshot%202026-01-13%20at%208.21.33%E2%80%AFAM.png', 'Analytics Dashboard')}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/yj5kr475_Screenshot%202026-01-13%20at%208.21.33%E2%80%AFAM.png" 
                    alt="Analytics Dashboard" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-2">
                <p className="text-xs text-white/50 text-center">Customer Feedback Form</p>
                <p className="text-xs text-white/50 text-center">Analytics Dashboard</p>
              </div>
            </div>

            {/* FAQ */}
            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h4 className="font-semibold text-white mb-4">FAQ in Notion</h4>
              <div className="space-y-2 mb-4">
                {['What ingredients are in Body Fruit products?', 'How do I track my order?', 'What is your return policy?'].map((q) => (
                  <div key={q} className="p-3 rounded-lg flex items-center gap-2 bg-white/5">
                    <MessageCircleQuestion className="w-4 h-4 text-white/40" />
                    <span className="text-sm text-white/70">{q}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg text-sm" style={{ background: `${colors.pink}20`, color: colors.pink }}>
                Use Notion AI to help craft FAQ answers
              </div>
            </div>
          </motion.div>
        </section>

        {/* ========== Q&A ========== */}
        <section ref={el => sectionRefs.current[6] = el} className="min-h-screen flex items-center justify-center px-4 py-20">
          <motion.div className="max-w-2xl w-full text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            
            <SectionHeader title="Q&A" duration="10 minutes" />
            
            {/* QR Code + Data Dreaming Logo */}
            <div className="flex flex-col items-center gap-6 mb-8">
              <div className="rounded-3xl p-8 inline-block" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-48 h-48 rounded-xl flex items-center justify-center mx-auto mb-4 p-2" style={{ background: colors.pink }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/9fndz4gv_QR%20CODE.png" 
                    alt="Scan to subscribe" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-white font-medium mb-1">Join our community for more tools + resources</p>
                <p className="text-white/50 text-sm mb-6">Stay updated on AI tips, templates, and workshops</p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_business-ai-guide/artifacts/myl1pon8_datadreaminglogowhite.png" 
                  alt="Data Dreaming" 
                  className="h-16 object-contain mx-auto"
                />
              </div>
            </div>
            
            <div className="mb-12">
              <a href="https://datadreaming.beehiiv.com/subscribe?utm_source=irl&utm_medium=workshop&utm_campaign=womens_business_soiree&utm_content=la" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-white shadow-lg" style={{ background: colors.coral }}>
                <Mail className="w-5 h-5" />
                Get More Resources
              </a>
            </div>

            {/* Hosted By Section - with actual logos - all on one line */}
            <div className="pt-8 border-t border-white/10">
              <p className="text-sm mb-4 sm:mb-6" style={{ color: colors.pink }}>Hosted By</p>
              <div className="flex justify-center items-center gap-4 sm:gap-8">
                <img src={logos.oceanAI} alt="Ocean AI" className="h-10 sm:h-16 object-contain" />
                <img src={logos.wines1010} alt="1010 Wines" className="h-10 sm:h-16 object-contain" />
                <img src={logos.oldvine} alt="Oldvine Florals" className="h-10 sm:h-16 object-contain" />
              </div>
            </div>
          </motion.div>
        </section>
      </div>

      {/* Lightbox for fullscreen images */}
      <ImageLightbox 
        src={lightboxImage.src} 
        alt={lightboxImage.alt} 
        isOpen={lightboxImage.isOpen} 
        onClose={closeLightbox} 
      />
    </div>
  );
};

export default WorkshopPage;
